import React from 'react';
import { motion } from 'framer-motion';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Divider,
  Grid,
  Avatar,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { formatValue, getSourceColor, getFieldIcon, groupDataByCategory } from '../utils/dataUtils';
import LocationMap from './LocationMap';

const UserDetailCard = ({ user }) => {
  if (!user) return null;

  const displayName = user.first_name || user.last_name 
    ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
    : user.username || 'Unknown User';
  
  const source = user.source || 'Unknown Source';
  const sourceColor = getSourceColor(source);
  const avatarLetter = displayName.charAt(0).toUpperCase();
  
  // Group data by categories for better organization
  const groupedData = groupDataByCategory(user);
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  // Render a field with icon and label
  const renderField = (fieldName, value) => {
    const iconName = getFieldIcon(fieldName);
    const label = fieldName.replace(/_/g, ' ').toUpperCase();
    
    // Special handling for location fields
    if (fieldName === 'first_known_location' || fieldName === 'last_known_location') {
      return (
        <Paper 
          elevation={0}
          sx={{ 
            p: 3,
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            borderRadius: 2,
            height: '100%',
            overflow: 'hidden'
          }}
        >
          {/* Field header with icon and label */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            {/* Icon wrapper */}
            <Box 
              sx={{ 
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: `${sourceColor}20`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1.5,
                flexShrink: 0,
                color: sourceColor
              }}
            >
              {/* Material Icon as direct span element */}
              <span className="material-icons" style={{ fontSize: '18px', lineHeight: 1 }}>
                {iconName}
              </span>
            </Box>
            
            {/* Field label */}
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontWeight: 600,
                color: 'text.primary',
                letterSpacing: '0.5px'
              }}
            >
              {label}
            </Typography>
          </Box>
          
          {/* Map component for location data */}
          <Box sx={{ mt: 2 }}>
            <LocationMap 
              locationData={value} 
              title={label} 
              color={fieldName === 'first_known_location' ? 'green' : 'red'}
            />
          </Box>
        </Paper>
      );
    }
    
    // Regular field rendering
    return (
      <Paper 
        elevation={0}
        sx={{ 
          p: 3,
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          borderRadius: 2,
          height: '100%',
          overflow: 'hidden'
        }}
      >
        {/* Field header with icon and label */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          {/* Icon wrapper */}
          <Box 
            sx={{ 
              width: 32,
              height: 32,
              borderRadius: '50%',
              backgroundColor: `${sourceColor}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.5,
              flexShrink: 0,
              color: sourceColor
            }}
          >
            {/* Material Icon as direct span element */}
            <span className="material-icons" style={{ fontSize: '18px', lineHeight: 1 }}>
              {iconName}
            </span>
          </Box>
          
          {/* Field label */}
          <Typography 
            variant="subtitle2" 
            sx={{ 
              fontWeight: 600,
              color: 'text.primary',
              letterSpacing: '0.5px'
            }}
          >
            {label}
          </Typography>
        </Box>
        
        {/* Field value */}
        <Typography 
          variant="body1" 
          sx={{ 
            pl: 5.5,
            color: 'text.secondary',
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap'
          }}
        >
          {formatValue(value)}
        </Typography>
      </Paper>
    );
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <Card 
        elevation={4}
        sx={{
          position: 'relative',
          overflow: 'visible',
          mb: 4,
          borderRadius: 3,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '8px',
            height: '100%',
            backgroundColor: sourceColor,
            borderTopLeftRadius: 12,
            borderBottomLeftRadius: 12,
          }
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            mb: 3,
            gap: 2
          }}>
            <Avatar 
              sx={{ 
                bgcolor: sourceColor,
                width: 70,
                height: 70,
                boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
                border: `3px solid ${sourceColor}`,
              }}
            >
              {avatarLetter}
            </Avatar>
            <Box>
              <Typography 
                variant="h4" 
                component="h1" 
                gutterBottom
                sx={{
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  hyphens: 'auto'
                }}
              >
                {displayName}
              </Typography>
              <Chip
                label={source}
                sx={{
                  backgroundColor: sourceColor,
                  color: '#fff',
                  fontWeight: 'bold',
                  px: 1,
                }}
              />
            </Box>
          </Box>
          
          <Divider sx={{ mb: 4 }} />
          
          {Object.entries(groupedData).map(([category, fields], categoryIndex) => (
            <motion.div
              key={category}
              variants={itemVariants}
              transition={{ duration: 0.3, delay: categoryIndex * 0.1 }}
            >
              <Accordion 
                defaultExpanded={categoryIndex === 0}
                sx={{ 
                  mb: 2,
                  backgroundColor: 'background.paper',
                  '&:before': { display: 'none' },
                  boxShadow: 'none',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.03)',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <Typography variant="h6">{category}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {Object.entries(fields).map(([fieldName, value]) => (
                      <Grid 
                        item 
                        xs={12} 
                        sm={fieldName === 'first_known_location' || fieldName === 'last_known_location' ? 12 : 6}
                        md={fieldName === 'first_known_location' || fieldName === 'last_known_location' ? 6 : 4}
                        key={fieldName}
                      >
                        {renderField(fieldName, value)}
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </motion.div>
          ))}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default UserDetailCard; 